import { useState, useEffect, useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PillTabs, TableChip } from '../../components/PremiumDesign';
import { V2Table } from '@/common/components';
import { Button, ConfirmDeleteModal, SearchInput } from '@/components';
import { PlusIcon } from '../../components/Icons';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import { getCRMTags, createCRMTag, deleteCRMTag, updateCRMTag } from '../api';
import { parseApiError } from '@/common/utils';
import { useDelayedSkeleton } from '@/common/hooks';

const TAG_COLORS = [
  '#F37021',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#64748B',
];

export function TagsTab({
  companyId,
  isReadOnly = false,
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  tabs,
  activeTab,
  onTabChange,
}) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTag, setEditingTag] = useState(null);
  const [previewingTag, setPreviewingTag] = useState(null);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState(TAG_COLORS[0]);
  const [apiErrors, setApiErrors] = useState({});
  const [tagToDelete, setTagToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const isEditMode = !!editingTag;
  const isPreviewMode = !!previewingTag && !isEditMode;
  const isCustomTagColor = !TAG_COLORS.some(
    (color) => color.toLowerCase() === tagColor.toLowerCase(),
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const {
    data: tagsResponse,
    isLoading: isInitialLoading,
    isFetching,
  } = useQuery({
    queryKey: ['crm-tags', companyId, page, debouncedSearchQuery],
    queryFn: () =>
      getCRMTags(companyId, { page, search: debouncedSearchQuery }),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && tagsResponse !== undefined,
    300,
    500,
  );
  const isLoading = isInitialLoading || showDelayedSkeleton;

  const tags = tagsResponse?.results || tagsResponse || [];
  const totalCount = tagsResponse?.count || 0;

  const resetForm = () => {
    setEditingTag(null);
    setPreviewingTag(null);
    setTagName('');
    setTagColor(TAG_COLORS[0]);
    setApiErrors({});
  };

  const createMutation = useMutation({
    mutationFn: (payload) => {
      if (!canCreate) throw new Error('Permission denied.');
      return createCRMTag({ companyId, payload });
    },
    onSuccess: () => {
      toast.success('Tag created successfully!');
      queryClient.invalidateQueries(['crm-tags', companyId]);
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-filter-fields', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-tags', companyId],
      });
      resetForm();
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to create tag.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ tagId, payload }) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return updateCRMTag({ companyId, tagId, payload });
    },
    onSuccess: () => {
      toast.success('Tag updated successfully!');
      queryClient.invalidateQueries(['crm-tags', companyId]);
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-filter-fields', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-tags', companyId],
      });
      resetForm();
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to update tag.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (tagId) => {
      if (!canDelete) throw new Error('Permission denied.');
      return deleteCRMTag({ companyId, tagId });
    },
    onSuccess: () => {
      toast.success('Tag deleted successfully!');
      queryClient.invalidateQueries(['crm-tags', companyId]);
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-filter-fields', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-tags', companyId],
      });
      setTagToDelete(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete tag.');
      setTagToDelete(null);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const startEditing = (tag) => {
    setEditingTag(tag);
    setPreviewingTag(null);
    setTagName(tag.name || tag);
    setTagColor(tag.color || TAG_COLORS[0]);
    setApiErrors({});
  };

  const startPreview = (tag) => {
    setPreviewingTag(tag);
    setEditingTag(null);
    setTagName(tag.name || tag);
    setTagColor(tag.color || TAG_COLORS[0]);
    setApiErrors({});
  };

  const handleSubmit = () => {
    if (!tagName.trim() || isReadOnly || isSaving) return;

    const payload = {
      name: tagName.trim(),
      color: tagColor,
    };

    if (editingTag) {
      const tagId = editingTag.id;
      if (tagId) {
        updateMutation.mutate({ tagId, payload });
      } else {
        toast.error('Editing not supported for this tag structure.');
      }
    } else {
      createMutation.mutate(payload);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Tag Name',
        defaultWidth: 280,
        minWidth: 160,
        cell: (tag) => (
          <TableChip
            tone="primary"
            style={{
              backgroundColor: `${tag.color || TAG_COLORS[0]}20`,
              borderColor: tag.color || TAG_COLORS[0],
              color: tag.color || TAG_COLORS[0],
            }}
          >
            {tag.name || tag}
          </TableChip>
        ),
      },
      {
        id: 'actions',
        label: 'Action',
        defaultWidth: 120,
        minWidth: 96,
        grow: false,
        align: 'center',
        stopPropagation: true,
        cell: (tag) => (
          <div className="flex items-center justify-center gap-3">
            {canUpdate && (
              <button
                type="button"
                onClick={() => startEditing(tag)}
                className="hover:opacity-80 transition-opacity shrink-0"
                title="Edit Tag"
              >
                <img
                  src={editIcon}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => handleDeleteClick(tag)}
                className="hover:opacity-80 transition-opacity shrink-0"
                title="Delete Tag"
              >
                <img
                  src={trashIcon}
                  alt=""
                  className="w-5 h-5 object-contain shrink-0"
                />
              </button>
            )}
          </div>
        ),
      },
    ],
    [canUpdate, canDelete],
  );

  const handleDeleteClick = (tag) => {
    const tagId = tag.id;
    if (tagId) {
      setTagToDelete(tag);
    } else {
      toast.error('Deleting not supported for this tag structure.');
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Create/Edit Tag form header - Fixed */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0 mb-4">
        <h4 className="text-[22px] leading-[28px] font-bold font-sf-pro-display text-[#1F2241]">
          {editingTag
            ? 'Edit Tag'
            : isPreviewMode
              ? 'Tag Preview'
              : 'Create Tag'}
        </h4>
        {tabs && (
          <PillTabs items={tabs} value={activeTab} onChange={onTabChange} />
        )}
      </div>

      {/* Lower content - Full Scroll */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar flex flex-col gap-4 pb-8 pr-1">
        <div className="bg-form-background-light rounded-[16px] p-4 shrink-0">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
            <div className="flex flex-col gap-2 flex-1 min-w-0 max-w-md">
              <label className="text-sm font-medium text-text-secondary">
                Tag<span className="text-status-error">*</span>
              </label>
              <input
                type="text"
                value={tagName}
                onChange={(e) => {
                  setTagName(e.target.value);
                  setApiErrors((prev) => ({ ...prev, name: null }));
                }}
                placeholder="e.g. VIP Customer, Hot Lead"
                disabled={isReadOnly || isSaving || isPreviewMode}
                className={`w-full h-11 px-4 rounded-xl bg-white border text-text-primary text-sm placeholder:text-text-muted focus:outline-none transition-colors ${apiErrors?.name ? 'border-status-error focus:border-status-error' : 'border-border-default focus:border-primary'} ${isReadOnly || isPreviewMode ? 'opacity-70 cursor-not-allowed' : ''}`}
              />
              {apiErrors?.name && (
                <p className="text-status-error text-xs font-medium">
                  {apiErrors.name}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <label className="text-sm font-medium text-text-secondary">
                Tag Colour<span className="text-status-error">*</span>
              </label>
              <div className="flex items-center gap-3 flex-wrap">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    disabled={isReadOnly || isSaving || isPreviewMode}
                    onClick={() => setTagColor(color)}
                    className={`w-8 h-8 rounded-full cursor-pointer border-2 transition-all disabled:cursor-not-allowed ${
                      tagColor.toUpperCase() === color
                        ? 'border-text-primary scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}

                {/* Custom Color Picker */}
                <div
                  className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-all disabled:cursor-not-allowed ${isReadOnly || isSaving || isPreviewMode ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${
                    isCustomTagColor
                      ? 'border-2 border-text-primary scale-110'
                      : 'border-2 border-transparent hover:scale-105'
                  }`}
                  style={{
                    background: isCustomTagColor
                      ? tagColor
                      : 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                  }}
                  title="Custom Colour"
                >
                  <input
                    type="color"
                    value={tagColor}
                    disabled={isReadOnly || isSaving || isPreviewMode}
                    onChange={(e) => setTagColor(e.target.value)}
                    className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer opacity-0 disabled:cursor-not-allowed"
                    aria-label="Custom tag colour"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full shrink-0">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tags by name"
            className="w-full sm:max-w-md sm:flex-1 order-2 sm:order-1"
            inputClassName="h-[44px]"
          />

          {isEditMode ? (
            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 sm:ml-auto order-1 sm:order-2">
              <Button
                variant="secondary"
                onClick={resetForm}
                disabled={isSaving}
                className="h-[44px] px-4 flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSaving || !tagName.trim()}
                className="h-[44px] px-4 flex-1 sm:flex-none"
              >
                {isSaving ? 'Saving...' : 'Save Change'}
              </Button>
            </div>
          ) : isPreviewMode ? (
            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 sm:ml-auto order-1 sm:order-2">
              <Button
                variant="secondary"
                onClick={resetForm}
                className="h-[44px] px-4 flex-1 sm:flex-none"
              >
                Close
              </Button>
              {canUpdate && (
                <Button
                  variant="primary"
                  onClick={() => startEditing(previewingTag)}
                  className="h-[44px] px-4 flex-1 sm:flex-none"
                >
                  Edit Tag
                </Button>
              )}
            </div>
          ) : (
            canCreate && (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSaving || isReadOnly || !tagName.trim()}
                className="h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto order-1 sm:order-2"
                icon={<PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
              >
                <span className="whitespace-nowrap">
                  {isSaving ? 'Adding...' : 'Add Tag'}
                </span>
              </Button>
            )
          )}
        </div>

        {/* Table */}
        <div className="w-full min-w-0 flex flex-col flex-1 min-h-0">
          <V2Table
            tight
            storageKey="crm_tags_table_column_widths"
            columns={columns}
            data={Array.isArray(tags) ? tags : []}
            rowKey={(tag, index) => tag.id || index}
            loading={isLoading || !tagsResponse}
            onRowClick={(tag) => startPreview(tag)}
            isRowSelected={(tag) =>
              (editingTag && editingTag.id === tag.id) ||
              (previewingTag && previewingTag.id === tag.id)
            }
            empty="No tags found"
            pagination={{
              currentPage: page,
              totalCount,
              itemsPerPage: 10,
              onChange: setPage,
            }}
          />
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={!!tagToDelete}
        title="Delete Tag"
        description="This tag and its information will be permanently deleted."
        onConfirm={() => deleteMutation.mutate(tagToDelete.id)}
        onCancel={() => setTagToDelete(null)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
